import { ApplicationUI } from "./ApplicationUI";
import { ApplicationState } from "./constants";
import { Notification } from "./Notification";
import { v4 as uuidv4 } from "uuid";
import * as machinemotion from "../api/machinemotion";
import widgets from "../legacy/widgets";
import { ApplicationTypes } from "./constants/ApplicationTypes";
import environment from "../environment";
import helpers, { simpleClone } from "../helpers";
import { action, computed, makeObservable, observable } from "mobx";


/**
 * @typedef {Object} ExecutionState
 * @property {ApplicationState} state The current application state
 * @property {string} application The application name
 * @property {Object} fsmStates
 * @property {Object} inputs
 * @property {Object[]} logging
 * @property {Object} outputs
 * @property {Object[]} robotSimulation
 * @property {object} sequences
 * @property {Object[]} simulation
 * @property {Object} variables
 */

/**
 * Represent a generic Application that is playable by the execution engine.
 */
export class ApplicationBase extends Notification {

  /**
   * @param {import('./Library').Library} pLibrary MachineLogic Library
   * @param {ApplicationTypes} pType Type of the Application
   * @param {boolean} pIsValidateable If set to true, the Validator will validate the Application. Defaults to "false".
   * @param {boolean} pIsPlayable If set to true, this application will appear in the app launcher and UI builder. Defualts to "false".
   */
  constructor(pLibrary, pType, pIsValidateable = false, pIsPlayable = false) {
    super();

    this.mLibrary = pLibrary;
    this.mType = pType;
    this.mIsValidateable = pIsValidateable;
    this.mIsPlayable = pIsPlayable;
    this.mName = '';
    /** @type ApplicationState */
    this.mState = ApplicationState.INACTIVE;
    this.mStatusPollingProcessId = undefined;
    this.mDirty = false;
    this.mUI = new ApplicationUI();
    this.mId = uuidv4();
    this.mUUID = undefined; // TODO: should be named mHash on day
    this.mDeployedByMachineCloud = false;
    this.isAppDebugMode = false;
    // isChanged flag is the same as mDirty except that mDirty is debounced (async) whereas isChange is sync thus set to true immediately
    this.isChanged = false; // used in the CAD
    this.robotParameters = {};

    makeObservable(this,{
      mState: observable,
      mLibrary: observable,
      mName: observable,
      id: computed,
      name: computed,
      active: computed,
      state: computed,
      setName: action,
      setState: action,
    });
  }

  getApplicationVersionHash() {
    throw new Error('Applications MUST define this function themselves');
  }
  /**
   * If we are on the pendant AND the Application was deployed by machine cloud, it is readonly.
   * @returns {boolean}
   */
  isReadOnly() {
    return this.mDeployedByMachineCloud && environment.isOnPendant();
  }

  get isValidateable() {
    return this.mIsValidateable;
  }

  get isPlayable() {
    return this.mIsPlayable;
  }

  /**
   * If we are in c loud AND the Application was deployed by machine cloud, it is deployed to the edge.
   * @returns {boolean}
   */
  isDeployedToEdge() {
    return this.mDeployedByMachineCloud && environment.isOnCad();
  }

  getLibrary() {
    return this.mLibrary;
  }

  getId() {
    return this.mId;
  }

  get id() {
    return this.mId;
  }

  /**
   * @deprecated Use ApplicationBase.name getter
   */
  getName() {
    return this.mName;
  }

  getType() {
    return this.mType;
  }

  /**
   * @param {pCustomTimeOut}
   */
  setDirty(pCustomTimeOut = null, pBeforeDeploy = null) {
    // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
    const debouncedFunction = helpers.debounce(() => {
      this.mLibrary.getValidator().validateApp(this);

      if (!this.mDirty) {  // No need to stack multiple deploys
        this.mDirty = true;

        if (pBeforeDeploy) pBeforeDeploy();
        // Deploy app if needed
        // Set timeout to deploy app after 300ms
        this.deploy();
      }
    }, pCustomTimeOut !== null ? pCustomTimeOut : 300);

    return debouncedFunction();
  }

  setName(pNewName) {
    if(this.mName === pNewName) {
      return;
    }

    const lOldName = this.mName;

    this.mName = this.mLibrary.uniqueApplicationName(pNewName);

    const onComplete = () => {
      this.setDirty();
      this.notify('onNameChanged', this.mName);
    };

    machinemotion.library(
      (pApplicationList) => {
        if (pApplicationList && pApplicationList.includes(lOldName)) {
          machinemotion.rename(lOldName, this.toJson(), (pData) => {
            if (pData) {
              this.mUUID = pData.uuid;
              onComplete();
            } else {
              this.mName = lOldName;
              this.notify('onNameChanged', this.mName);
            }
          });
        } else {
          onComplete();
        }
      },
      () => {
        this.mName = lOldName;
        this.notify('onNameChanged', this.mName);
      }
    );
  }

  getUi() {
    return this.mUI;
  }

  hasUi() {
    // Note (mkosarek) UI is considered not empty if at least one widget is present in the UI.
    // Warning: This function gets referenced by the App Launcher in the mm-pendant repo. Do not delete it
    // or else the App Launcher will break.
    return this.getUi() != undefined && this.getUi().listTabs().some((pTab) => pTab.listWidgets().length > 0);
  }

  /**
   * Upserts the application to the execution engine.
   * @param {object} pCompletion JSON of the Application
   */
  deploy(pCompletion) {
    if (this.isReadOnly()) {
      this.mDirty = false;
      this.isChanged = false;
      console.warn(`Application ${this.mName} is read only, deployment will be skipped.`);
      if (pCompletion) { pCompletion(this.toJson()); }
      return;
    }

    // TODO: Why do we reach out to the backend if we already have the apps on the frontend?
    machinemotion.getApplications((appList) => {
      const existingApp = appList.find( app => app.id === this.mId);
      if (!existingApp){
        machinemotion.deploy(this.toJson(),
          (pData) => {
            if(pData) {
              this.mUUID = pData.uuid;
              this.mDirty = false;
              this.notify('onDeployed');
            }
            else {
            // Deployment failed. Allow following deployments.
              this.mDirty = false;
            }

            this.isChanged = false;
            if(pCompletion) pCompletion(pData);
          }
        );
      } else if (this.mDirty) {
        machinemotion.update(this.toJson(), (pData) => {
          if(pData) {
            this.mDirty = false;
            this.notify('onUpdated');
          }
          else if (pData == null){
            // Update failed. Allow following updates.
            this.mDirty = false;
          }
          this.isChanged = false;
          if(pCompletion) pCompletion(pData);
        });
      } else {
        if(pCompletion) pCompletion(this.toJson());
      }
    },
    (pError) => {
      if (pError.error && pError.xhr.responseText == "Wrong url, access denied or instance doesn't exist...") {
        // Note:
        //   - this responseText is produced by the vention_assembler/server, in the 'passthrough' route, when our token has expired...
        //     which is exactly the case we want to recover from
        //   - we may want to change this responseText to a more specific error message, at both ends
        this.notify('requestReconnect');
        if(pCompletion) {pCompletion(null);}
      }
    });
  }

  /**
   * Start polling the application status.
   * Only one request will be sent at a time.
   *
   * You can specify a minimum amount of time to wait between each request.
   * We will wait until minIntervalMs has been elapsed since the previous request was initiated.
   * If the previous request took more time to complete than the minimum specified interval, the next request will be sent immediately.
   *
   * @param {number} [minIntervalMs] The minimum amount of time between each request in milliseconds. Defaults to 250ms.
   */
  startPolling(minIntervalMs = 250) {
    const poll = (pollingStatusId) => {
      if (pollingStatusId !== this.mStatusPollingProcessId) {
        // This polling process was canceled
        return;
      }

      const requestStartedAt = new Date();
      this.status(() => {
        const timeElapsed = new Date() - requestStartedAt;
        const nextRequestWaitTime = Math.max(0, minIntervalMs - timeElapsed);

        if (this.getState() === ApplicationState.RUNNING || this.getState === ApplicationState.PAUSED) {
          setTimeout(() => poll(pollingStatusId), nextRequestWaitTime);
        }
      });
    };

    // We only allow a single polling process
    if (!this.mStatusPollingProcessId) {
      this.mStatusPollingProcessId = uuidv4();
      poll(this.mStatusPollingProcessId);
    }
  }

  stopPolling() {
    this.mStatusPollingProcessId = undefined;
  }

  reset() {
    const _lThis = this;
    this.stopPolling();
    if (this.mState != ApplicationState.COMPLETED) {
      machinemotion.stop(this.mName, function(pData) { _lThis.setState(pData); });
      this.mState = ApplicationState.COMPLETED;
    }
    this.notify('onStateChanged', this.mState);
    this.notify('onTrace', null);
  }

  /**
   * @param {ExecutionState} executionState
   */
  setState(executionState) {
    // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
    if (executionState === null || executionState === undefined) {
      console.warn('Application [' + this.mName + '] received an invalid state update.');
      this.reset();
      return;
    }

    if (executionState.application !== this.mName) {
      console.warn('Application [' + this.mName + '] received a state update for application [' + executionState.application + ']');
      this.reset();
      return;
    }

    this.notify('onTrace', executionState);  // Notify each time as status may be the same but the current sequence/instruction may change

    if (this.mState !== executionState.state) {
      this.mState = executionState.state;
      this.isActive() ? this.startPolling() : this.stopPolling();
      this.notify('onStateChanged', this.mState);   // Only notify on rising edge
    }

    if (executionState.hasOwnProperty('simulation') && executionState.simulation.length > 0 && !this.isPaused()) {
      this._simulate(executionState);
    }

    if (this.mState === ApplicationState.COMPLETED) {
      this.onEndActive();
    }
  }

  getState() { return this.mState; }

  isRunning() { return this.mState === ApplicationState.RUNNING; }

  isPaused() { return this.mState === ApplicationState.PAUSED; }

  isActive() { return this.isRunning() || this.isPaused(); }

  get active() { return this.isActive(); }

  onBeginActive() {}

  onEndActive() {}

  canPlayRobotApp() {
    return !this.mLibrary.robotAppsService.selectors.isRobotNotSimulatable();
  }

  hasRobotSimulateInstruction() {
    return false;
  }

  /**
   * @param pRunTimeConfiguration
   * @param {(ExecutionState) => void} [pOnRequestSent] A callback to be called when the play request has been sent.
   */
  play(pRunTimeConfiguration, pOnRequestSent = undefined) {
    // WARNING: as of #2044, this 'play' function is entirely superseded in simulation, by the vention_assembler's machinelogic.js; the implementation here is not reused.

    if (this.hasRobotSimulateInstruction() && !this.canPlayRobotApp()) {
      widgets.messageBox('Your robot is not connected. Go to the configuration pane to connect your robot', 'error');
      return;
    }

    if (this.isActive()) {
      console.warn('Application [' + this.mName + '] running already.');

      return;
    }

    this.onBeginActive();
    this.deploy(pData => {
      if (!pData) {
        return;
      }

      // Let's warn the user if there is any errors for the application
      const lWarnings = this.mLibrary.getValidator().getAllWarningsForApp(this);
      if (lWarnings.length) {
        widgets.messageBox('Your application contains warnings. We recommend fixing them before playing.', 'warning');
      }

      machinemotion.play(this.mId, pRunTimeConfiguration, pApplicationStatus => {
        if (pApplicationStatus) {
          this.setState(pApplicationStatus);
        }
        else {
          widgets.messageBox('Failed to play Application "' + this.getName() + '". Please check that all of your MachineMotion controller(s) are connected.', 'error');
        }

        pOnRequestSent?.(pApplicationStatus);
      });
    });
  }

  /**
   * @param {(ExecutionState) => void} [pOnRequestSent] A callback to be called when the pause request has been sent.
   */
  pause(pOnRequestSent = undefined) {
    if (this.mState !== ApplicationState.RUNNING) {
      return;
    }

    machinemotion.pause(this.mName, pApplicationStatus => {
      this.setState(pApplicationStatus);
      pOnRequestSent?.(pApplicationStatus);
    });
  }

  /**
   * @param {(ExecutionState) => void} [pOnRequestSent] A callback to be called when the resume request has been sent.
   */
  resume(pOnRequestSent = undefined) {
    if (this.mState !== ApplicationState.PAUSED) {
      return;
    }

    machinemotion.resume(this.mName, pApplicationStatus => {
      this.setState(pApplicationStatus);
      pOnRequestSent?.(pApplicationStatus);
    });
  }

  /**
   * @param {(ExecutionState) => void} [pOnRequestSent] A callback to be called when the stop request has been sent.
   */
  stop(pOnRequestSent = undefined) {
    // WARNING : This function is overridden in the assembler (vention_assembler/client/src/machinelogic.js)
    if (!this.isActive()) {
      return;
    }

    this.isAppDebugMode = false;
    machinemotion.stop(this.mName, pApplicationStatus => {
      this.setState(pApplicationStatus);
      pOnRequestSent?.(pApplicationStatus);
    });
  }

  /**
   * @param {(ExecutionState) => void} [pOnStatusReceived] A callback to be called when the status request has been sent.
   */
  status(pOnStatusReceived = undefined) {
    machinemotion.status(pApplicationStatus => {
      this.setState(pApplicationStatus);
      pOnStatusReceived?.(pApplicationStatus);
    });
  }

  _simulate(/*pData*/) {}

  getAsDownloadFormat() {
    const app = this.toJson();
    // When we upload and download the apps, we clear the deployedByMachineCloud flag
    app.deployedByMachineCloud = false;

    return {
      applications: [this.mLibrary.getConfigManager().toJson(), app]
    };
  }

  static fromJson(pApp, pJson) {
    pApp.mName = pJson.name;
    pApp.mUI = new ApplicationUI(pJson.ui);
    pApp.mId = pJson.id;
    pApp.mUUID = pJson.uuid;
    pApp.mDeployedByMachineCloud = pJson.deployedByMachineCloud ? pJson.deployedByMachineCloud : false;
    if(pApp.mDeployedByMachineCloud && environment.isOnCad()) { console.warn(`Application "${pApp.mName}" has read only mode in CAD.`); }
  }

  static get DATA_TYPE() {
    return 'machinelogic_application_base';
  }

  cloneToNewJson() {
    const lJson = simpleClone(this.toJson());
    lJson.name = this.getLibrary().uniqueApplicationName(this.getName() + ' Copy');
    lJson.deployedByMachineCloud = false;
    lJson.id = uuidv4();
    return lJson;
  }

  toJson() {
    return {
      type :this.mType,
      name : this.mName,
      id: this.mId,
      ui: this.mUI && this.mUI.toJson(),
      deployedByMachineCloud: this.mDeployedByMachineCloud,

      // NOTE: In the CAD, we need a way of identifying different types of vertices.
      // Up until this time, we have done that by checking if the 'sequences' field exists as
      // a parameter on the vertex. This is obviously bad. As such, I propose the following field
      // which will ensure that our vertices have a defined "type".
      __vertexType__: ApplicationBase.DATA_TYPE
    };
  }
}

Object.defineProperty(
  ApplicationBase.prototype,
  'name',
  {
    get: function () {
      return this.mName;
    }
  }
);

Object.defineProperty(
  ApplicationBase.prototype,
  'state',
  {
    get: function () {
      return this.getState();
    }
  }
);
